import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import {
  FiHome,
  FiCalendar,
  FiUsers,
  FiUserPlus,
  FiSettings,
  FiInbox,
  FiLogOut,
  FiMenu,
  FiX,
} from "react-icons/fi";
import { apiGet } from "./api";
import notificationService from "./notificationService";
import "./Layout.css";

// Sign Out Confirmation Modal Component
function SignOutModal({ onConfirm, onCancel }) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-header">Are you sure?</h3>
        <p>Are you sure you want to sign out?</p>
        <div className="modal-actions">
          <button onClick={onCancel} className="btn btn-secondary">Cancel</button>
          <button onClick={onConfirm} className="btn btn-danger">Yes, sign out</button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const nav = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const location = useLocation();

  // Effect to fetch notifications
useEffect(() => {
  const fetchNotifications = async () => {
    try {
      const data = await apiGet("/inbox/notifications");
      setNotificationCount(data.count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  fetchNotifications(); // Only fetches once when component mounts (login/refresh)
  
  // Keep the notification service for manual updates
  const handleNotificationUpdate = () => fetchNotifications();
  notificationService.subscribe("notificationsUpdated", handleNotificationUpdate);

  return () => {
    notificationService.unsubscribe("notificationsUpdated", handleNotificationUpdate);
  };
}, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isSidebarOpen && window.innerWidth <= 768) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
  }, [isSidebarOpen]);

  const handleSignOutClick = () => {
    setShowSignOutModal(true);
  };

  const confirmSignOut = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setShowSignOutModal(false);
    nav("/auth/react/signin");
  };

  const cancelSignOut = () => {
    setShowSignOutModal(false);
  };
  
  const navItems = [
    { icon: <FiHome />, text: "Home", path: "/app/react/home" },
    { icon: <FiCalendar />, text: "My Schedule", path: "/app/react/my-schedule" },
    { icon: <FiUsers />, text: "Find Classmates", path: "/app/react/find-classmates" },
    { icon: <FiUserPlus />, text: "My Friends", path: "/app/react/my-friends" },
  ];

  const sidebarMode = window.innerWidth > 768 ? (isSidebarOpen ? "expanded" : "collapsed") : (isSidebarOpen ? "expanded" : "collapsed");

  return (
    <div className="layout-container">
      <div 
        className={`sidebar-overlay ${isSidebarOpen && window.innerWidth <= 768 ? "active" : ""}`}
        onClick={() => setIsSidebarOpen(false)}
      ></div>
      
      {window.innerWidth <= 768 && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="toggle-btn"
        >
          {isSidebarOpen ? <FiX /> : <FiMenu />}
        </button>
      )}

      <aside className={`sidebar ${sidebarMode}`}>
        <div className="sidebar-header">
          <h1 className="logo-text">
            {isSidebarOpen || window.innerWidth <= 768 ? "ShareMySchedule" : "SMS"}
          </h1>
          {window.innerWidth > 768 && (
             <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="toggle-btn"
            >
              {isSidebarOpen ? <FiX /> : <FiMenu />}
            </button>
          )}
        </div>
        <nav className="sidebar-nav">
          <ul>
            {navItems.map((item) => (
              <li key={item.text}>
                <NavLink to={item.path} className="nav-link" title={item.text}>
                  {item.icon}
                  <span className="nav-text">{item.text}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="sidebar-footer">
          <NavLink to="/app/react/inbox" className="nav-link" title="Inbox">
            <FiInbox />
            <span className="nav-text">Inbox</span>
            {notificationCount > 0 && (
              <span className="sidebar-badge">{notificationCount}</span>
            )}
          </NavLink>
          <NavLink
            to="/app/react/account-settings"
            className="nav-link"
            title="Account Settings"
          >
            <FiSettings />
            <span className="nav-text">Account Settings</span>
          </NavLink>
          <div className="nav-link" onClick={handleSignOutClick} title="Sign Out">
            <FiLogOut />
            <span className="nav-text">Sign Out</span>
          </div>
        </div>
      </aside>
      <main className="main-content">
        <header className="top-bar">
          <div className="welcome-message">
            <h2>Welcome back!</h2>
          </div>
          <div className="top-bar-actions">
            <button className="top-bar-btn sign-out" onClick={handleSignOutClick}>
              <FiLogOut />
              <span>Sign Out</span>
            </button>
          </div>
        </header>
        <div className="page-content">
          <Outlet />
        </div>
      </main>
      
      {showSignOutModal && (
        <SignOutModal
          onConfirm={confirmSignOut}
          onCancel={cancelSignOut}
        />
      )}
    </div>
  );
}